import { DASHBOARD_VIEW_TYPE } from '@adtrackify/at-tracking-event-types';
import { Gauge, LayoutGrid, Plus, Search } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { NAVIGATION_LIST_ITEM_STATE, NAVIGATION_LIST_ROOT_AS } from '@/components/ui/types/navigation-list-variants-enums';
import { NavigationList } from '@/components/ui/navigation-list';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SidebarInput } from '@/components/ui/sidebar/sidebar-primitives';
import { useSidebar } from '@/components/ui/sidebar/hooks/use-sidebar';
import { sidebarIcon, sidebarIconButton } from '@/components/ui/sidebar/helpers/sidebar-theme';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/browser/use-is-mobile';
import { selectViewIdForPixel, useDashboardSelectionStore } from '@/features/dashboard/stores/use-dashboard-selection-store';
import { useDashboardUIStore } from '@/features/dashboard/stores/dashboard-ui-store';
import { useDashboardBootstrap } from '@/features/dashboard/hooks/use-dashboard-bootstrap';
import { resolveDashboardViewLabel } from '@/features/dashboard/helpers/view-helpers';
import { SUMMARY_VIEW_NAME } from '@/features/dashboard/constants/view-name-constants';
import { openNamedDashboardView } from '@/features/dashboard/helpers/pending-view-selection';
import { useAccountState } from '@/contexts/account/account-context';

const VIEWS_PATH = '/board/views';
const MAX_VISIBLE_VIEWS = 4;
const VIEW_ROW_HEIGHT_PX = 28;
const VIEW_ROW_GAP_PX = 6;
const VIEW_LIST_MAX_HEIGHT_PX =
  MAX_VISIBLE_VIEWS * VIEW_ROW_HEIGHT_PX + (MAX_VISIBLE_VIEWS - 1) * VIEW_ROW_GAP_PX;

function useViewsNavChrome() {
  const location = useLocation();
  const { toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();
  const { pixelId } = useAccountState();
  const selectedViewId = useDashboardSelectionStore(state =>
    selectViewIdForPixel(state, pixelId)
  );
  const setSelectedViewId = useCallback(
    (viewId: string | null) => {
      useDashboardSelectionStore.getState().setSelectedViewId(viewId, pixelId ?? undefined);
    },
    [pixelId]
  );
  const { data: bootstrapData } = useDashboardBootstrap(pixelId);
  const views = bootstrapData?.viewsByType?.[DASHBOARD_VIEW_TYPE.VIEW];
  const summaryView = views?.find(view => view.name === SUMMARY_VIEW_NAME);
  const firstCustomView = views?.find(view => view.name !== SUMMARY_VIEW_NAME);
  const isOnOverview = location.pathname === VIEWS_PATH;
  const isSummarySelected = Boolean(summaryView && selectedViewId === summaryView.id);
  const isSummaryActive = isOnOverview && isSummarySelected;
  const isViewsHubActive = isOnOverview && !isSummarySelected;

  const handleLinkClick = () => {
    if (isMobile) {
      toggleSidebar();
    }
  };

  return {
    handleLinkClick,
    isOnOverview,
    isSummaryActive,
    isViewsHubActive,
    summaryView,
    firstCustomView,
    views,
    setSelectedViewId,
  };
}

export function OverviewSummaryPin({ isCollapsed }: { isCollapsed: boolean }) {
  const { handleLinkClick, isSummaryActive, views, setSelectedViewId } = useViewsNavChrome();

  const openSummary = () => {
    openNamedDashboardView(views, SUMMARY_VIEW_NAME, setSelectedViewId);
    handleLinkClick();
  };

  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={VIEWS_PATH}
            onClick={openSummary}
            aria-label={SUMMARY_VIEW_NAME}
            aria-current={isSummaryActive ? 'page' : undefined}
            className={sidebarIconButton({
              state: isSummaryActive ? 'active' : 'default',
            })}
          >
            <Gauge className={sidebarIcon()} aria-hidden />
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right">{SUMMARY_VIEW_NAME}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <NavigationList.Root as={NAVIGATION_LIST_ROOT_AS.DIV} aria-label={SUMMARY_VIEW_NAME}>
      <NavigationList.Item
        asChild
        state={
          isSummaryActive
            ? NAVIGATION_LIST_ITEM_STATE.ACTIVE
            : NAVIGATION_LIST_ITEM_STATE.DEFAULT
        }
      >
        <Link
          to={VIEWS_PATH}
          onClick={openSummary}
          aria-current={isSummaryActive ? 'page' : undefined}
        >
          <Gauge className={sidebarIcon()} aria-hidden />
          <span className="truncate">{SUMMARY_VIEW_NAME}</span>
        </Link>
      </NavigationList.Item>
    </NavigationList.Root>
  );
}

export function OverviewViewsHub({ isCollapsed }: { isCollapsed: boolean }) {
  const {
    handleLinkClick,
    isViewsHubActive,
    isSummaryActive,
    firstCustomView,
    setSelectedViewId,
  } = useViewsNavChrome();

  const openViews = () => {
    if (isSummaryActive && firstCustomView) {
      setSelectedViewId(firstCustomView.id);
    }
    handleLinkClick();
  };

  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={VIEWS_PATH}
            onClick={openViews}
            aria-label="Views"
            aria-current={isViewsHubActive ? 'page' : undefined}
            className={sidebarIconButton({
              state: isViewsHubActive ? 'active' : 'default',
            })}
          >
            <LayoutGrid className={sidebarIcon()} aria-hidden />
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right">Views</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <NavigationList.Root as={NAVIGATION_LIST_ROOT_AS.DIV} aria-label="Views">
      <NavigationList.Item
        asChild
        state={
          isViewsHubActive
            ? NAVIGATION_LIST_ITEM_STATE.ACTIVE
            : NAVIGATION_LIST_ITEM_STATE.DEFAULT
        }
      >
        <Link
          to={VIEWS_PATH}
          onClick={openViews}
          aria-current={isViewsHubActive ? 'page' : undefined}
        >
          <LayoutGrid className={sidebarIcon()} aria-hidden />
          <span className="truncate">Views</span>
        </Link>
      </NavigationList.Item>
    </NavigationList.Root>
  );
}

export function OverviewSavedViewsSection({ isCollapsed = false }: { isCollapsed?: boolean }) {
  const navigate = useNavigate();
  const { toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();
  const { pixelId } = useAccountState();
  const selectedViewId = useDashboardSelectionStore(state =>
    selectViewIdForPixel(state, pixelId)
  );
  const setSelectedViewId = useCallback(
    (viewId: string | null) => {
      useDashboardSelectionStore.getState().setSelectedViewId(viewId, pixelId ?? undefined);
    },
    [pixelId]
  );
  const openCreateViewDialog = useDashboardUIStore((state) => state.openCreateViewDialog);
  const { isOnOverview } = useViewsNavChrome();

  const { data: bootstrapData, isLoading } = useDashboardBootstrap(pixelId);
  const views = bootstrapData?.viewsByType?.[DASHBOARD_VIEW_TYPE.VIEW]?.filter(
    view => view.name !== SUMMARY_VIEW_NAME
  );

  const [searchQuery, setSearchQuery] = useState('');
  const savedViewsHeadingId = useId();

  const filteredViews = useMemo(() => {
    if (!views) return [];
    if (!searchQuery.trim()) return views;
    const query = searchQuery.toLowerCase().trim();
    return views.filter(view =>
      resolveDashboardViewLabel(view).toLowerCase().includes(query)
    );
  }, [views, searchQuery]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollIndicator = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const hasMore = el.scrollHeight - el.scrollTop - el.clientHeight > 1;
    setCanScrollDown(hasMore);
  }, []);

  const scrollContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      scrollRef.current = node;
      if (!node) return;

      updateScrollIndicator();
      const observer = new ResizeObserver(() => {
        updateScrollIndicator();
      });
      observer.observe(node);
      const content = node.firstElementChild;
      if (content) {
        observer.observe(content);
      }
      resizeObserverRef.current = observer;
    },
    [updateScrollIndicator]
  );

  useEffect(() => {
    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
  }, []);

  if (isCollapsed) {
    return null;
  }

  const handleViewClick = (viewId: string) => {
    setSelectedViewId(viewId);
    if (!isOnOverview) {
      void navigate(VIEWS_PATH);
    }
    if (isMobile) {
      toggleSidebar();
    }
  };

  const handleCreateView = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOnOverview) {
      void navigate(VIEWS_PATH);
    }
    setTimeout(() => {
      openCreateViewDialog();
    }, 100);
  };

  return (
    <NavigationList.Root as={NAVIGATION_LIST_ROOT_AS.DIV} aria-label="Saved views">
      {/*
        Heading + Group are Root siblings so gap-3.5 owns G-HCHILD / R14.
        Create stays chrome-only next to the non-interactive Heading (P-SECTION-LABEL).
      */}
      <div className="flex w-full items-center gap-1.5">
        <NavigationList.Heading
          id={savedViewsHeadingId}
          className="min-w-0 flex-1 text-content-secondary"
        >
          Saved views
        </NavigationList.Heading>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Create new view"
              onClick={handleCreateView}
              className="flex h-5 w-5 items-center justify-center rounded-control text-content-tertiary transition-[color] duration-fast ease-standard pointer-coarse:min-h-11 pointer-coarse:min-w-11 pointer-fine:hover:text-content-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus-ring motion-reduce:transition-none"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-xs">
            Create new view
          </TooltipContent>
        </Tooltip>
      </div>

      <NavigationList.Group aria-labelledby={savedViewsHeadingId}>
        {!isLoading && views && views.length > MAX_VISIBLE_VIEWS ? (
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-content-tertiary"
              aria-hidden
            />
            <SidebarInput
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search views..."
              aria-label="Search views"
              className="pl-7 pr-2 type-field-label placeholder:text-content-tertiary"
            />
          </div>
        ) : null}

        {isLoading ? (
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-[28px] w-full rounded-control" />
            <Skeleton className="h-[28px] w-4/5 rounded-control" />
            <Skeleton className="h-[28px] w-full rounded-control" />
          </div>
        ) : filteredViews.length > 0 ? (
          <div className="relative">
            <ScrollArea
              style={{ maxHeight: VIEW_LIST_MAX_HEIGHT_PX }}
              viewportRef={scrollContainerRef}
              onViewportScroll={updateScrollIndicator}
              viewportClassName="max-h-[inherit] w-full"
            >
              <div className="flex flex-col gap-1.5">
                {filteredViews.map((view) => {
                  const isViewActive = isOnOverview && selectedViewId === view.id;
                  return (
                    <NavigationList.Item
                      key={view.id}
                      asChild
                      state={
                        isViewActive
                          ? NAVIGATION_LIST_ITEM_STATE.ACTIVE
                          : NAVIGATION_LIST_ITEM_STATE.DEFAULT
                      }
                    >
                      <button
                        type="button"
                        onClick={() => handleViewClick(view.id)}
                        aria-current={isViewActive ? 'page' : undefined}
                        className="w-full truncate text-left"
                        title={resolveDashboardViewLabel(view)}
                      >
                        {resolveDashboardViewLabel(view)}
                      </button>
                    </NavigationList.Item>
                  );
                })}
              </div>
            </ScrollArea>
            {canScrollDown ? (
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-surface-navigation to-transparent" />
            ) : null}
          </div>
        ) : searchQuery ? (
          <p className="px-2.5 py-1 type-body text-content-secondary">No matching views</p>
        ) : (
          <p className="px-2.5 py-1 type-body text-content-secondary">No views yet</p>
        )}
      </NavigationList.Group>
    </NavigationList.Root>
  );
}
